import React, { useRef, useState } from 'react';
import { SCRUBBER_API_BASE } from '../api/config';
import { csrfFetch } from '../api/csrf';

export default function FileUploadPanel({
  onFileUpload,
  onValidationComplete,
  selectedTable,
  headerCorrections
}) {
  const fileInputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) await processFile(file);
  };

  const processFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('universeType', selectedTable?.universeType || '');
    formData.append('headerCorrections', JSON.stringify(headerCorrections || {}));

    setIsUploading(true);
    try {
      const response = await csrfFetch(`${SCRUBBER_API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      let result = null;
      try { result = await response.json(); } catch (_) { /* non-JSON body */ }

      if (!response.ok) {
        const msg = (result && (result.error || result.message)) || `Upload failed (HTTP ${response.status})`;
        // 401 = no Core session; 429 = upload rate-limited; 400 = bad universe type / missing file
        alert(`Upload failed: ${msg}`);
        return;
      }

      if (onFileUpload) onFileUpload(file);
      if (onValidationComplete) onValidationComplete(result);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error.message || 'network error'} — check that you are signed in and on az-scrubber.precisioncompliancegroup.com.`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className="group w-full text-center rounded-2xl p-8 border-2 border-dashed border-slate-300 bg-white hover:border-rose-400 transition-all duration-200"
    >
      <div className="space-y-4">
        <div className="text-3xl opacity-50">⬆️</div>
        <h3 className="text-lg font-bold text-slate-800">
          Upload file for{' '}
          <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 text-sm font-semibold rounded-full border border-slate-200">
            {selectedTable?.short}
          </span>
        </h3>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Click below or drag and drop your file here
        </p>
        <button
          onClick={() => fileInputRef.current.click()}
          disabled={isUploading}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-rose-500 to-orange-600 transition-opacity shadow-lg shadow-rose-500/25 ${isUploading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8l-4 4m0 0l-4-4" />
          </svg>
          {isUploading ? 'Uploading…' : 'Select File'}
        </button>
        <div className="flex items-center justify-center gap-3 mt-3">
          <span className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-xs font-medium text-slate-500">Excel (.xlsx)</span>
          <span className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-xs font-medium text-slate-500">CSV (.csv)</span>
        </div>
      </div>
      <input type="file" accept=".csv,.xlsx" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
    </div>
  );
}
